import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  FilterQuery,
  ProjectFields,
  useAsyncAutocomplete,
  YesObject
} from '@yes.technology/react-toolkit'

import fetchYesObjects from 'yesObject/shared/api/fetchYesObjects'
import useFetchSuggestions from '../useFetchSuggestions'

type UseSuggestionsOnChangeProps = {
  objectclassUuid: string
  initialValue?: string
  uuidCurrentSitemodelObjectclass?: string
  skipRelations?: boolean
  skipMetadata?: boolean
  projectFields?: ProjectFields
  interdependentFilteringQuery?: FilterQuery
}

const useSuggestionsOnChange = ({
  objectclassUuid,
  initialValue = '',
  uuidCurrentSitemodelObjectclass = '',
  skipRelations = false,
  skipMetadata = false,
  projectFields,
  interdependentFilteringQuery
}: UseSuggestionsOnChangeProps) => {
  const [value, onChange] = useState(initialValue)

  useEffect(() => {
    onChange(initialValue)
  }, [initialValue])

  const fetchSuggestions = useFetchSuggestions({
    objectclassUuid,
    fetchYesObjects,
    uuidCurrentSitemodelObjectclass,
    skipRelations,
    skipMetadata,
    projectFields,
    interdependentFilteringQuery
  })

  const [stickySuggestions, setStickySuggestions] = useState<YesObject[]>([])

  const resetValue = useCallback((newValue = '') => {
    onChange(newValue)
    if (newValue === '') {
      setStickySuggestions([])
    }
  }, [])

  const { isLoading, suggestions: fetchedSuggestions } = useAsyncAutocomplete({
    query: value,
    fetchSuggestions
  })

  useEffect(() => {
    if (fetchedSuggestions.length === 0) {
      return
    }

    setStickySuggestions((previous) => {
      const hasSameSuggestions =
        previous.length === fetchedSuggestions.length &&
        previous.every(
          (suggestion, index) =>
            suggestion.uuid === fetchedSuggestions[index]?.uuid
        )

      return hasSameSuggestions ? previous : fetchedSuggestions
    })
  }, [fetchedSuggestions])

  const suggestions = useMemo(() => {
    if (fetchedSuggestions.length > 0) {
      return fetchedSuggestions
    }

    if (value.trim() === '') {
      return stickySuggestions
    }

    return fetchedSuggestions
  }, [fetchedSuggestions, stickySuggestions, value])

  return {
    value,
    onChange,
    isLoading,
    suggestions,
    resetValue
  }
}

export default useSuggestionsOnChange
